/**
 * Can these invoices be edited as ONE?
 *
 * WHY THIS EXISTS
 * `invoice_history.items` is the case-wide mediation LEDGER, not one party's
 * bill. Each party is charged a share of it. So a shared editor does not write
 * "the same line items onto N invoices" — it rewrites ONE ledger that N invoices
 * each take a slice of. That only makes sense when those invoices genuinely
 * describe the same session. Offering it when they do not would silently replace
 * one party's distinct bill with another's.
 *
 * The rule is therefore about the LEDGER, never about the amounts. Amounts
 * legitimately differ between parties that share a session: split remainders
 * (326.67 / 326.67 / 326.66), card processing surcharges, and admin fees that
 * one side is charged and the other has absorbed.
 *
 * Mirrors resolveHourlyPartyShare in the backend orchestrator. Deliberately does
 * NOT reuse FixedUpdateInvoice's haveIdenticalLineItems: that compares
 * description + amount only, so 2 × $500 and 4 × $250 read as identical — right
 * for flat fixed lines, wrong for hours × rate, and editing it would change the
 * fixed editor.
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100 + Number.EPSILON) / 100

const parseMaybeJson = (value) => {
    if (Array.isArray(value)) return value
    if (typeof value !== 'string') return null
    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : null
    } catch (_) {
        return null
    }
}

/** Why this invoice cannot be edited at all, or null when it can. */
export const blockedReasonFor = (row) => {
    if (!row) return 'Invoice not found.'
    if (row.status === 'paid') return 'Already paid'
    if (row.status === 'paid_manually') return 'Marked paid'
    return null
}

/** The stored ledger and what it sums to. */
export const ledgerFor = (row) => {
    const items = parseMaybeJson(row?.invoice_history?.items)
    if (!items || !items.length) return { items: null, sum: 0 }
    return {
        items,
        sum: round2(items.reduce((s, it) => s + (Number(it?.amount) || 0), 0)),
    }
}

/**
 * The admin fee this invoice actually carries.
 * A coupon with an override charges that specific fee rather than nothing.
 */
export const effectiveAdminFeeFor = (row, caseInfo) => {
    const override = caseInfo?.coupon?.admin_fee_override_amount
    if (override != null && override !== '') return Math.round(Number(override) || 0)
    if (caseInfo?.coupon_id) return 0
    return Math.round(Number(row?.admin_fee || 0))
}

/**
 * What fraction of the ledger this party is billed, and whether the admin fee
 * is inside their amount. Same four readings the backend uses — on production
 * the processing fee sits INSIDE send_to.amount on 277 of 354 pending rows and
 * outside on 49, so both are tried before falling back to the percentage.
 */
export const resolveShare = (row, caseInfo) => {
    const clamp = (x) => Math.min(1, Math.max(0.0001, Math.round(x * 1e6) / 1e6))
    const fallbackAdminInAmount = Math.round(Number(row?.admin_fee || 0)) > 0

    const { sum: ledger } = ledgerFor(row)
    if (!ledger || ledger <= 0) {
        return { share: 1, adminInAmount: fallbackAdminInAmount, resolved: false }
    }

    const sendTo = parseMaybeJson(row?.invoice_history?.send_to)
    const entry = sendTo
        ? sendTo.find((e) => String(e?.client_id) === String(row?.client_id))
        : null

    if (entry) {
        const sa = Number(entry.amount || 0)
        const pf = Number(row?.processing_fee || 0)
        const adm = Math.round(Number(row?.admin_fee || 0))
        const amt = Number(row?.amount || 0)
        const near = (a) => Math.abs(a) <= 0.05

        if (near(amt - sa)) return { share: clamp(sa / ledger), adminInAmount: false, resolved: true }
        if (near(amt - adm - sa)) return { share: clamp(sa / ledger), adminInAmount: true, resolved: true }
        if (near(amt - pf - sa)) return { share: clamp(sa / ledger), adminInAmount: false, resolved: true }
        if (near(amt - adm - pf - sa)) return { share: clamp(sa / ledger), adminInAmount: true, resolved: true }

        /* WHEN THE STORED SPLIT NO LONGER RECONCILES.
         *
         * Do NOT fall straight to `entry.percentage`. That number is what the
         * split was MEANT to be, not what the party was actually billed, and
         * trusting it silently re-splits the case on any edit — including an
         * edit that changed nothing. Derive the share from what this row was
         * really charged, against the ledger it was charged on.
         *
         * THIS BRANCH IS NOT OPTIONAL POLISH — IT IS WHAT KEEPS THE PREVIEW
         * HONEST. scale-be's resolveHourlyPartyShare
         * (hourlyInvoiceUpdateOrchestrator.helper.js) has had it all along and
         * it is what actually decides the billed amount. Without the same
         * branch here the browser computes a DIFFERENT share from the same row,
         * and every figure built on it — Step 2's "each party is billed", Step
         * 3's tab, the InvoicePreview totals — overstates the invoice the client
         * receives. On UAT case 2829's shape ($2,000 ledger, send_to says 50%,
         * party really billed $850 + $250 admin) the percentage fallback
         * previewed $1,250 against a $1,100 bill; on a forked history carrying
         * an amount but no percentage it previewed $2,050 against $1,050.
         *
         * Party scope makes it worse rather than safer: the collapsed line
         * seeded from this share IS the bill scale-be issues verbatim (it forces
         * share 1 by design), so its own derived-share safety net is bypassed
         * and the browser's number becomes the charge.
         *
         * Keep this byte-for-byte with the backend. If one of them changes,
         * change both. */
        const derived = round2(amt - (fallbackAdminInAmount ? adm : 0) - pf) / ledger
        if (Number.isFinite(derived) && derived > 0 && derived <= 1.0001) {
            return {
                share: clamp(derived),
                // Still `false`: derived, not reconciled — same as the backend.
                adminInAmount: fallbackAdminInAmount,
                resolved: false,
            }
        }

        /* Only when the row itself cannot answer either. */
        const pct = Number(entry.percentage)
        if (pct > 0 && pct <= 100) {
            return { share: clamp(pct / 100), adminInAmount: fallbackAdminInAmount, resolved: false }
        }
    }

    return { share: 1, adminInAmount: fallbackAdminInAmount, resolved: false }
}

/** What this party is billed, given an edited ledger total. */
export const partyTotalFor = (row, caseInfo, ledgerTotal) => {
    const { share, adminInAmount } = resolveShare(row, caseInfo)
    const fee = effectiveAdminFeeFor(row, caseInfo)
    return round2(round2(Number(ledgerTotal || 0) * share) + (adminInAmount ? fee : 0))
}

/**
 * Every money figure for one party, from one call.
 *
 * Exists so the editor, the preview and the summary cannot disagree. They did:
 * both rendered `ledger + adminFee` as "Invoice total", which is the WHOLE
 * session rather than this party's share of it — on a 50/50 case that showed
 * $2,750 next to a document Stripe issues for $1,500.
 */
export const partyBreakdownFor = (row, caseInfo, ledgerTotal, override) => {
    /* `override` is how PARTY SCOPE is expressed. When the mediator is editing
     * one party's own bill, the ledger on screen IS that bill, so the share is 1
     * by construction rather than by inference — without this every display site
     * would re-resolve 0.25 against a private ledger and show a quarter of the
     * party's own invoice. */
    const { share, adminInAmount, resolved } = override
        ? { share: override.share, adminInAmount: override.adminInAmount, resolved: true }
        : resolveShare(row, caseInfo)
    const fee = effectiveAdminFeeFor(row, caseInfo)
    const ledger = round2(ledgerTotal)
    const base = round2(ledger * share)
    return {
        ledger,
        share,
        pct: Math.round(share * 100),
        base,
        fee,
        adminInAmount,
        total: round2(base + (adminInAmount ? fee : 0)),
        resolved,
    }
}

/** The single line the editor opens with when nothing was stored. */
export const reconstructedBaseFor = (row, caseInfo) =>
    round2(
        Math.max(
            0,
            Number(row?.amount || 0) -
                effectiveAdminFeeFor(row, caseInfo) -
                Number(row?.processing_fee || 0)
        )
    )

/** Order-sensitive fingerprint of a ledger: description, hours, rate. */
const normItems = (row) => {
    const { items } = ledgerFor(row)
    if (!items) return null
    return items
        .map((it) =>
            [
                String(it?.description || '').trim().toLowerCase().replace(/\s+/g, ' '),
                round2(it?.quantity),
                round2(it?.rate),
            ].join('|')
        )
        .join('~')
}

const dayOf = (v) => (v ? String(v).slice(0, 10) : '')

/**
 * @returns {{ok: boolean, reason: string|null, branch: string|null}}
 */
export const combinable = (rows, caseInfo) => {
    const list = Array.isArray(rows) ? rows.filter(Boolean) : []

    if (list.length < 2) {
        return { ok: false, reason: 'Only one invoice can be edited here.', branch: null }
    }
    if (list.some((r) => blockedReasonFor(r))) {
        return {
            ok: false,
            reason: 'One of these invoices is already paid, so they cannot be edited together.',
            branch: null,
        }
    }

    // Due dates must agree — the shared editor writes one date to all of them.
    if (new Set(list.map((r) => dayOf(r.due_date))).size > 1) {
        return {
            ok: false,
            reason: 'These invoices have different due dates. Edit them one party at a time.',
            branch: null,
        }
    }

    const historyIds = list.map((r) =>
        r.invoice_history_id != null ? String(r.invoice_history_id) : null
    )
    const allHaveHistory = historyIds.every((h) => h !== null)
    const noneHaveHistory = historyIds.every((h) => h === null)

    // 1. SAME LEDGER — the parties are literally billed off one session record.
    if (allHaveHistory && new Set(historyIds).size === 1) {
        const { sum } = ledgerFor(list[0])
        if (sum > 0) return { ok: true, reason: null, branch: 'shared' }
    }

    // 2. NO LEDGER ANYWHERE — nothing stored to compare, so fall back to the
    //    reconstructed base each invoice would open with. Roughly a quarter of
    //    live rows never stored their items.
    if (noneHaveHistory) {
        const bases = list.map((r) =>
            round2(
                Math.max(
                    0,
                    Number(r.amount || 0) -
                        effectiveAdminFeeFor(r, caseInfo) -
                        Number(r.processing_fee || 0)
                )
            )
        )
        if (bases.some((b) => b <= 0)) {
            return {
                ok: false,
                reason: 'These invoices have no stored line items and their totals cannot be rebuilt. Edit them one party at a time.',
                branch: null,
            }
        }
        if (new Set(bases.map(String)).size === 1) {
            return { ok: true, reason: null, branch: 'reconstructed' }
        }
        return {
            ok: false,
            reason: 'These parties were billed different amounts. Edit them one party at a time.',
            branch: null,
        }
    }

    // 3. DISTINCT LEDGERS that are provably the same bill. This is how a set
    //    stays combinable after an earlier edit forked its history rows.
    //    The share test is what stops two $800 ledgers billed 50/50 and 100/0
    //    from reading as equivalent.
    if (allHaveHistory) {
        const sigs = list.map(normItems)
        const sums = list.map((r) => ledgerFor(r).sum)
        const shares = list.map((r) => resolveShare(r, caseInfo).share)

        const sameItems = sigs.every((s) => s !== null) && new Set(sigs).size === 1
        const sameSum = sums.every((s) => s > 0) && new Set(sums.map((s) => s.toFixed(2))).size === 1
        const sameShare = shares.every((s) => Math.abs(s - shares[0]) <= 0.0001)

        if (sameItems && sameSum && sameShare) {
            return { ok: true, reason: null, branch: 'fork' }
        }
    }

    return {
        ok: false,
        reason: 'These invoices have different line items. Edit them one party at a time.',
        branch: null,
    }
}
